/**
 * Buffers listing impressions and ships them in batches.
 *
 * Impressions are the highest-volume signal in the product by an order of
 * magnitude -- roughly 20 cards on screen across 167,149 page views in 30 days
 * is on the order of 3M events, against 188,657 listing views. Two consequences
 * shape this module:
 *
 *   1. Only a session with an id logs them (see utils/impression-sampling.ts),
 *      and this queue drops everything else before it allocates.
 *   2. They go to their own endpoint and their own table, so an impression
 *      flood can never slow down or block the funnel, checkout and error paths
 *      that share the `events` batch.
 *
 * Within a page view the same card can enter the viewport repeatedly -- scroll
 * up, scroll down, a Swiper slide returning. Those are the same impression, so
 * each (listing, row, position) is recorded at most once per page view.
 */

import { isSessionSampled } from './impression-sampling';
import { getRankingVariant } from './ranking-variant-store';

export interface ListingImpression {
  listing_id: number;
  list_name?: string;
  result_position: number;
  row_index?: number;
  page_type?: string;
  total_results?: number;
  tags?: string[];
  category_id?: number;
  category_group_id?: number;
  data_source_id?: number;
  randomized?: number;
}

/** Ship when the buffer reaches this, so a long scroll does not hold everything in memory. */
const BATCH_SIZE = 25;

/** Or when this much time passes, so a short visit still reports. */
const FLUSH_INTERVAL_MS = 5000;

let buffer: ListingImpression[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let seen = new Set<string>();
let sessionId: string | null = null;
let anonymousId: string | null = null;
let sampled: boolean | null = null;

function key(impression: ListingImpression): string {
  return `${impression.listing_id}:${impression.list_name ?? ''}:${impression.result_position}`;
}

/**
 * Tell the queue which session it is buffering for.
 *
 * Called by the tracker rather than read from storage here: the sampling
 * decision must come from the SAME session id the click events carry, or the
 * two sides of the ratio disagree and the rate is silently wrong.
 */
export function configureImpressionQueue(id: string | null, anonId?: string | null): void {
  anonymousId = anonId ?? anonymousId;
  if (id === sessionId) return;
  sessionId = id;
  sampled = isSessionSampled(id);
  // A new session is a new page-view scope.
  seen = new Set();
}

/** Is the current session having its impressions logged? */
export function isImpressionSessionSampled(): boolean {
  return sampled === true;
}

/** Browser only: flushImpressions, its one caller, runs from an IntersectionObserver or an unload handler, and never with an empty batch. */
async function ship(rows: ListingImpression[]): Promise<void> {
  try {
    const body = JSON.stringify({
      session_id: sessionId,
      anonymous_id: anonymousId ?? '',
      // On the envelope, not the row: every impression in a batch was rendered
      // by one response, so they share an arm, and repeating the label 40 times
      // per beacon would cost bytes on the one path that runs during unload.
      ranking_variant: getRankingVariant(),
      impressions: rows,
    });

    // sendBeacon survives the page going away, which is exactly when the last
    // batch of a visit would otherwise be lost. It has no response to inspect,
    // so a failure here is genuinely fire-and-forget -- acceptable for a
    // sampled signal, and the fetch path below covers the normal case.
    if (typeof navigator.sendBeacon === 'function') {
      const ok = navigator.sendBeacon(
        '/api/analytics/impressions',
        new Blob([body], { type: 'application/json' })
      );
      if (ok) return;
    }

    await fetch('/api/analytics/impressions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    });
  } catch {
    // Never let an analytics failure surface in the page it is describing.
  }
}

export function flushImpressions(): void {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (buffer.length === 0) return;
  const rows = buffer;
  buffer = [];
  void ship(rows);
}

/**
 * Record that a card was actually seen.
 *
 * Silently ignored for an unsampled session, which is the common case -- the
 * check happens first so an unsampled visit costs one Set lookup and nothing
 * else.
 */
export function recordImpression(impression: ListingImpression): void {
  if (sampled !== true) return;
  if (!Number.isFinite(impression.listing_id) || impression.listing_id <= 0) return;

  const k = key(impression);
  if (seen.has(k)) return;
  seen.add(k);

  buffer.push(impression);

  if (buffer.length >= BATCH_SIZE) {
    flushImpressions();
    return;
  }

  if (!flushTimer) {
    flushTimer = setTimeout(flushImpressions, FLUSH_INTERVAL_MS);
  }
}

/** Test seam. */
export function resetImpressionQueueForTests(): void {
  buffer = [];
  seen = new Set();
  sessionId = null;
  anonymousId = null;
  sampled = null;
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
}
